'use client'

import { useEffect, useState } from 'react'
import Button from './ui/buttons/button'

type ErrorLike = Error & { digest?: string; statusCode?: number }

export default function ErrorRecoveryCard({
  error,
  reset,
  homeHref = '/',
  loginHref = '/login',
}: {
  error: ErrorLike
  reset: () => void
  homeHref?: string
  loginHref?: string
}) {
  const [errorDetails, setErrorDetails] = useState('')
  const [showDetails, setShowDetails] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [copied, setCopied] = useState(false)
  const maxRetries = 3

  const isServerError = error.statusCode ? error.statusCode >= 500 : false

  useEffect(() => {
    console.error('Error boundary caught:', error)
    setErrorDetails(
      JSON.stringify(
        {
          message: error.message || 'An unexpected error occurred',
          digest: error.digest,
          timestamp: new Date().toISOString(),
          url: typeof window !== 'undefined' ? window.location.href : '',
        },
        null,
        2,
      ),
    )
  }, [error])

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      setRetryCount((prev) => prev + 1)
      reset()
    }
  }

  const handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload()
  }

  const copyErrorDetails = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    navigator.clipboard.writeText(errorDetails).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const supportHref = `mailto:support@payk12.com?subject=${encodeURIComponent(
    'PayK12 error report',
  )}&body=${encodeURIComponent(`Error ID: ${error.digest || 'Unknown'}\n\n${errorDetails}`)}`

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="rounded-lg bg-white p-8 pt-6 shadow-xl">
        <div className="mb-4 flex h-16 items-center">
          <img src="/svg/logo-payk12.svg" alt="PayK12" className="h-12 w-auto" />
        </div>

        <div className="relative mt-4 items-center justify-center text-center text-sm">
          <span className="relative h-[1px] bg-white px-2 font-sans text-black/60">
            {isServerError ? "We're having technical difficulties" : 'Something went wrong'}
          </span>
          <div className="relative -top-[10px] -z-10 h-[1px] bg-black/20" />
        </div>

        <div className="mt-6 text-center">
          <h1 id="error-heading" className="text-xl font-semibold text-gray-900">
            {isServerError ? 'PayK12 is taking a short break' : 'This page hit a snag'}
          </h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            {isServerError
              ? "Our servers are experiencing issues. We're working to fix this as quickly as possible."
              : "We encountered an unexpected problem. The issue has been logged and we'll look into it."}
          </p>
          <p className="mt-2 text-sm leading-6 text-gray-500">Nothing is wrong with your account or payments.</p>
        </div>

        {retryCount > 0 && retryCount < maxRetries && (
          <div className="mt-4 rounded-md bg-amber-50 p-3 text-amber-800" role="alert">
            <p className="text-sm">
              Retry attempt {retryCount} of {maxRetries}. If it still fails, use login below.
            </p>
          </div>
        )}

        {retryCount >= maxRetries && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-red-800" role="alert">
            <p className="text-sm font-medium">Maximum retry attempts reached</p>
            <p className="mt-1 text-sm">Reload the page or go back to login.</p>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3" role="navigation" aria-label="Error recovery options">
          {retryCount < maxRetries ? (
            <Button
              onClick={handleRetry}
              variant="primary"
              size="lg"
              className="w-full normal-case"
              aria-label="Try again"
            >
              Try again
            </Button>
          ) : (
            <Button
              onClick={handleReload}
              variant="primary"
              size="lg"
              className="w-full normal-case"
              aria-label="Reload page"
            >
              Reload page
            </Button>
          )}
          <a
            href={loginHref}
            className="flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 shadow-sm transition-all hover:bg-gray-100"
            aria-label="Back to login"
          >
            Back to login
          </a>
        </div>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="rounded text-sm text-gray-500 underline hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-expanded={showDetails}
            aria-controls="error-details"
          >
            {showDetails ? 'Hide' : 'Show'} technical details
          </button>

          {showDetails && (
            <div id="error-details" className="mt-4 text-left">
              <div className="relative rounded-md bg-gray-50 p-4">
                <button
                  type="button"
                  onClick={copyErrorDetails}
                  className="absolute right-2 top-2 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-primary hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  aria-label="Copy error details to clipboard"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <pre className="max-h-40 overflow-auto pr-12 text-xs text-gray-600">{errorDetails}</pre>
              </div>
              <p className="mt-2 text-center text-xs text-gray-500">Error ID: {error.digest || 'Unknown'}</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 text-center text-sm text-gray-500">
        <p>
          Need a hand?{' '}
          <a
            href={supportHref}
            className="font-medium text-white underline decoration-white/40 underline-offset-2 hover:decoration-white"
            aria-label="Contact support via email"
          >
            Email support
          </a>
        </p>
        <p className="mt-2 text-xs text-white/70">
          Press <kbd className="rounded border border-white/30 bg-white/10 px-1.5 py-0.5 font-sans text-white">ESC</kbd>{' '}
          to go home
          <a href={homeHref} className="sr-only">
            Home
          </a>
        </p>
      </div>
    </div>
  )
}
